import { AsyncPipe, DatePipe } from '@angular/common';
import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';

import { FundOnHold } from '@shared/models';
import {
  TransactionFormActions,
  selectHasFundsOnHold,
  selectIsFohAmountExceeded,
  selectShouldShowFohSelection,
  transactionFormFeature,
} from '@shared/store';

import { FohSelectionModalComponent } from './foh-selection-modal/foh-selection-modal.component';

@Component({
  selector: 'app-create-transaction-foh-selection',
  templateUrl: './create-transaction-foh-selection.component.html',
  styleUrls: ['./create-transaction-foh-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RktIconComponent, AsyncPipe, DatePipe],
})
export class CreateTransactionFohSelectionComponent implements OnInit, AfterViewChecked, OnDestroy {
  private destroy$ = new Subject<void>();

  shouldShowFohSelection$ = this.store.select(selectShouldShowFohSelection);
  hasFundsOnHold$ = this.store.select(selectHasFundsOnHold);
  isFohAmountExceeded$ = this.store.select(selectIsFohAmountExceeded);
  selectedFoh$ = this.store.select(transactionFormFeature.selectSelectedFundOnHold);

  isFohEnabled = false;

  constructor(
    private ref: ChangeDetectorRef,
    private store: Store,
    private modalService: ModalService,
  ) {}

  ngOnInit() {
    this.loadFohWhenFromAccountChanges();
    this.syncFohEnabledState();
  }

  ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadFohWhenFromAccountChanges() {
    this.store
      .select(transactionFormFeature.selectFromAccount)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fromAccount) => {
          if (fromAccount?.id) {
            this.store.dispatch(TransactionFormActions.loadFundsOnHold({ financialAccountId: fromAccount.id }));
          } else {
            this.store.dispatch(TransactionFormActions.clearFundsOnHold());
          }
          this.isFohEnabled = false;
        },
      });
  }

  private syncFohEnabledState() {
    this.store
      .select(transactionFormFeature.selectSelectedFundOnHold)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (selectedFoh) => {
          if (selectedFoh) {
            this.isFohEnabled = true;
          }
        },
      });
  }

  onFohToggle() {
    this.isFohEnabled = !this.isFohEnabled;

    if (!this.isFohEnabled) {
      this.store.dispatch(TransactionFormActions.setSelectedFundOnHold({ fundOnHold: null }));
    }
  }

  onSelectFohClick() {
    this.store
      .select(transactionFormFeature.selectSelectedFundOnHold)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (selectedFundOnHold) => {
          const modalRef = this.modalService.open(FohSelectionModalComponent, {
            className: 'create-transaction-financial-account-modal foh-selection-modal',
          });

          modalRef.componentInstance.modalInitData({ selectedFundOnHold });

          modalRef.result.then(
            (result?: FundOnHold | null) => {
              if (result !== undefined) {
                this.store.dispatch(TransactionFormActions.setSelectedFundOnHold({ fundOnHold: result }));
              }
            },
            () => false,
          );
        },
      })
      .unsubscribe();
  }

  onClearFoh() {
    this.store.dispatch(TransactionFormActions.setSelectedFundOnHold({ fundOnHold: null }));
  }
}
